import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Award, Globe, Shield, Mail, Phone } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import AnimatedCounter from "@/components/AnimatedCounter";
import { getServicesByCategory } from "@/data/services";

const coreServices = getServicesByCategory("core");
const emergingServices = getServicesByCategory("emerging");
const lifecycleServices = getServicesByCategory("lifecycle");

const stats = [
  { value: "200+", label: "Projects Delivered" },
  { value: "50+", label: "Enterprise Clients" },
  { value: "10+", label: "Years Experience" },
  { value: "99.99%", label: "Uptime SLA" },
];

export default function HomePage() {
  usePageMeta(null, "Vaana BuildLogic Technologies delivers enterprise IT solutions, ERP systems, cloud infrastructure, cybersecurity, AR/VR, AI/ML, IoT automation, and AMC/CMC services. With 10+ years of experience and 200+ projects, we help businesses achieve secure, scalable digital transformation.");
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="relative min-h-[620px] flex items-center pt-20 wave-divider overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Server infrastructure" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0D72B8]/85" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-24 w-full">
          <div className="max-w-3xl">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl leading-tight font-['Sora'] font-medium text-white mb-8 animate-fade-in-up">
              Enterprise IT, ERP, Cloud & Cybersecurity Solutions for Modern Businesses
            </h1>
            <div className="flex flex-wrap gap-4 animate-fade-in-up animation-delay-200">
              <Link to="/contact" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 px-7 py-3 btn-outline-white text-sm font-semibold rounded-full">contact us</Link>
              <Link to="/services" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 px-7 py-3 bg-white text-[#0D72B8] text-sm font-semibold rounded-full hover:bg-gray-50 transition-all">our services</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services - Gradient Cards */}
      <section data-testid="services-overview" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#0D72B8] font-semibold mb-3">What We Do</p>
            <h2 className="text-4xl sm:text-5xl font-['Sora'] font-semibold text-[#111827]">Core IT Solutions for Enterprises</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreServices.map((s, i) => (
              <Link to={`/services/${s.slug}`} key={s.slug} data-testid={`service-card-${i}`}
                className={`group relative overflow-hidden rounded-2xl p-8 bg-gradient-to-br ${s.color} text-white transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-blue-500/20 h-[280px] flex flex-col justify-between`}>
                <div>
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm mb-5 group-hover:bg-white/25 transition-colors duration-300">
                    <s.icon size={28} className="text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-2xl font-['Sora'] font-semibold mb-3">{s.title}</h3>
                  <p className="text-white/80 text-sm leading-relaxed">{s.shortDesc}</p>
                </div>
                <div className="mt-6 flex items-center gap-2 text-white/70 text-sm font-medium group-hover:text-white transition-colors">
                  Learn more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
                <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/5 group-hover:bg-white/10 transition-colors duration-500" />
                <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full bg-white/5 group-hover:bg-white/10 transition-colors duration-500" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Emerging Technologies - Cards with tags */}
      <section className="py-24 md:py-32 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#0D72B8] font-semibold mb-3">Emerging Technologies</p>
            <h2 className="text-4xl sm:text-5xl font-['Sora'] font-semibold text-[#111827]">Emerging Technologies for Digital Transformation</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {emergingServices.map((s, i) => (
              <Link to={`/services/${s.slug}`} key={s.slug} data-testid={`emerging-card-${i}`}
                className="group relative bg-white border border-gray-200 rounded-2xl p-8 transition-all duration-400 hover:-translate-y-2 hover:shadow-xl hover:shadow-blue-500/10 hover:border-[#0D72B8]/30 overflow-hidden h-[320px] flex flex-col">
                {s.tag && <span className="inline-block px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#0D72B8] bg-blue-50 rounded-full mb-5">{s.tag}</span>}
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#0D72B8] to-[#2B8AD4] mb-5">
                  <s.icon size={22} className="text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-['Sora'] font-bold text-[#111827] mb-3">{s.title}</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed mb-5 flex-grow">{s.shortDesc}</p>
                <span className="inline-flex items-center gap-2 text-[#0D72B8] text-sm font-medium group-hover:gap-3 transition-all">
                  Learn more <ArrowRight size={14} />
                </span>
                <div className="absolute -right-6 -bottom-6 w-24 h-24 rounded-full bg-blue-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Lifecycle & Educational - Side by side with lists */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[0.2em] uppercase text-[#0D72B8] font-semibold mb-3">Lifecycle & Operations</p>
            <h2 className="text-4xl sm:text-5xl font-['Sora'] font-semibold text-[#111827]">Enterprise Infrastructure & Operations Support</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {lifecycleServices.map((s, i) => (
              <Link to={`/services/${s.slug}`} key={s.slug} data-testid={`lifecycle-card-${i}`}
                className="group relative bg-gradient-to-br from-[#0D72B8] to-[#0B5898] rounded-2xl p-10 text-white overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/20 h-[340px] flex flex-col">
                <div className="relative z-10 flex flex-col h-full">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-white/15 mb-6">
                    <s.icon size={28} className="text-white" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-2xl font-['Sora'] font-semibold mb-4">{s.title}</h3>
                  <p className="text-white/75 text-sm leading-relaxed mb-4 flex-grow">{s.shortDesc}</p>
                  <span className="inline-flex items-center gap-2 text-white/70 text-sm font-medium group-hover:text-white transition-colors">
                    Learn more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
                <div className="absolute -right-10 -bottom-10 w-40 h-40 rounded-full bg-white/5" />
                <div className="absolute right-10 top-8 w-16 h-16 rounded-full bg-white/5" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-24 md:py-32 bg-[#F9FAFB] border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl sm:text-5xl font-['Sora'] font-semibold text-[#111827] mb-6 leading-tight">About Vaana BuildLogic Technologies</h2>
              <p className="text-[#6B7280] leading-relaxed mb-4">Looking for a solution to streamline your IT operations with enterprise-grade security and cloud infrastructure?</p>
              <p className="text-[#6B7280] leading-relaxed mb-8">Vaana BuildLogic Technologies is a New Delhi-based enterprise IT company delivering secure, scalable and future-ready digital solutions. With over a decade of experience, we specialize in IT infrastructure, ERP implementation, cloud services, cybersecurity, AR/VR development, AI/ML automation, IoT solutions, multimedia production and 3D printing. Our certified team has delivered 200+ projects with a 99.99% uptime SLA, helping businesses modernize operations, enhance security and accelerate digital transformation.</p>
              <Link to="/about" className="inline-flex items-center gap-2 px-7 py-3 btn-outline-blue text-sm font-semibold rounded-full">learn more <ArrowRight size={14} /></Link>
            </div>
            <div><img src="https://images.pexels.com/photos/7964413/pexels-photo-7964413.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Our team" className="rounded-2xl w-full object-cover h-96 shadow-lg" /></div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div data-testid="stat-0" className="text-center">
              <p className="text-4xl md:text-5xl font-['Sora'] font-bold text-[#0D72B8] mb-2"><AnimatedCounter end={200} suffix="+" /></p>
              <p className="text-sm text-[#6B7280]">Projects Delivered</p>
            </div>
            <div data-testid="stat-1" className="text-center">
              <p className="text-4xl md:text-5xl font-['Sora'] font-bold text-[#0D72B8] mb-2"><AnimatedCounter end={50} suffix="+" /></p>
              <p className="text-sm text-[#6B7280]">Enterprise Clients</p>
            </div>
            <div data-testid="stat-2" className="text-center">
              <p className="text-4xl md:text-5xl font-['Sora'] font-bold text-[#0D72B8] mb-2"><AnimatedCounter end={10} suffix="+" /></p>
              <p className="text-sm text-[#6B7280]">Years Experience</p>
            </div>
            <div data-testid="stat-3" className="text-center">
              <p className="text-4xl md:text-5xl font-['Sora'] font-bold text-[#0D72B8] mb-2"><AnimatedCounter end={99.99} suffix="%" /></p>
              <p className="text-sm text-[#6B7280]">Uptime SLA</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="relative py-24 md:py-32 wave-divider overflow-hidden">
        <div className="absolute inset-0 bg-[#0D72B8]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl font-['Sora'] font-semibold text-white mb-8 leading-tight">You're in good company</h2>
            <p className="text-blue-100 leading-relaxed mb-6">We build long-term relationships with our clients, resulting in an impressive portfolio of enterprise partnerships.</p>
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <Link to="/contact" data-testid="cta-contact-button" className="px-7 py-3 bg-white text-[#0D72B8] text-sm font-semibold rounded-full hover:bg-gray-50 transition-all shadow-lg">contact us</Link>
              <Link to="/about" className="px-7 py-3 btn-outline-white text-sm font-semibold rounded-full">more about us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { icon: Globe, title: "World-Class Infrastructure", points: ["Enterprise-grade hardware","Fully redundant network","High availability solutions","24/7 monitoring & support"] },
              { icon: Award, title: "A Decade of Excellence", points: ["200+ projects delivered","Trusted by 50+ enterprises","Certified professionals","Long-term partnerships"] },
              { icon: Shield, title: "Security & Compliance", points: ["ISO standard processes","Data protection ready","Regular security audits","Compliance-ready infrastructure"] },
            ].map((item, i) => (
              <div key={i} data-testid={`differentiator-${i}`} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-5"><item.icon size={36} className="text-[#0D72B8]" strokeWidth={1.5} /></div>
                <h3 className="text-2xl font-['Sora'] font-semibold text-[#111827] mb-5">{item.title}</h3>
                <ul className="space-y-2 text-left max-w-xs mx-auto">
                  {item.points.map((p, j) => (<li key={j} className="flex items-center gap-2 text-sm text-[#6B7280]"><CheckCircle size={14} className="text-[#0D72B8] flex-shrink-0" /> {p}</li>))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support CTA */}
      <section className="bg-[#0D72B8] py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="text-3xl font-['Sora'] font-semibold text-white mb-2">A support team backed by a decade of experience.</h3>
              <p className="text-blue-100 text-sm">Dedicated professionals capable of solving any technical challenge.</p>
            </div>
            <div className="flex gap-4 flex-shrink-0">
              <a href="mailto:info@vaanabuildlogictechnologies.com" className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#0D72B8] text-sm font-semibold rounded-full hover:bg-gray-50 transition-all"><Mail size={16} /> Email</a>
              <a href="tel:9953167073" className="inline-flex items-center gap-2 px-6 py-3 btn-outline-white text-sm font-semibold rounded-full"><Phone size={16} /> Phone</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
